import { Injectable, Optional } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { INotificationData, canDisplayAlertEventType, normalizeAlert } from "../../shared/models/be/notification";
import { IUnit } from "../../shared/models/be/unit";
import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { environment } from "../../../environments/environment";
import { ToastService } from "./utils/toast.service";
import { IonicColors } from "../../shared/enums/ionicColors.enum";
import { LoggerService } from "./utils/logger.service";
import { RequestsService } from "./utils/requests.service";
import { MonitoringPreferencesService } from "./monitoring-preferences.service";
import { IBedStateUpdate, canViewBedStates, isBedState } from "../../shared/models/be/bed-state";

/////////////////////////////////////////////////////////
//                                                     //
//                   ATTENZIONE                        //
//    Servizio utilizzato con providedIn: 'root'       //
//    Questa configurazione indica ad Angular che      //
//  il servizio deve essere fornito a livello della    //
//   root dell'applicazione. Ciò significa che verrà   //
//   creata una singola istanza del servizio che sarà  //
//  condivisa da tutti i componenti e gli altri        //
//              servizi che lo richiedono              // 
//                                                     //
//          NON RICHIAMARE HTTPCLIENT,                 //
//        NON PASSEREBBE PER L'INTERCEPTOR             //
//                                                     //
/////////////////////////////////////////////////////////


@Injectable({ providedIn: 'root' })
export class SharedService {
    public readonly connected$ = new Subject<void>();
    private readonly bedStateUpdates = new Subject<IBedStateUpdate>();
    public readonly bedStateUpdates$ = this.bedStateUpdates.asObservable();
    public get canViewBedStates(): boolean { return canViewBedStates(this.requestsService.getCurrentUser()); }
    private readonly locallyManagedAlert = new Subject<INotificationData>();
    public readonly locallyManagedAlert$ = this.locallyManagedAlert.asObservable();
    public get activeAlertCount(): number {
        return this.monitoringPreferences ? this.monitoringPreferences.counts().active : this.alertsList.length;
    }

    private _unitsTree = new BehaviorSubject<(IUnit & {isOpen: boolean})[]>([]);
    public unitsTree$ = this._unitsTree.asObservable();
    private _alertsList = new BehaviorSubject<INotificationData[]>([]);
    public alertsList$ = this._alertsList.asObservable();
    private _newAlert = new BehaviorSubject<INotificationData | undefined>(undefined);
    public newAlert$ = this._newAlert.asObservable();
    private _managedAlert = new BehaviorSubject<INotificationData | undefined>(undefined);
    public managedAlert$ = this._managedAlert.asObservable();
    public showMatGridLines: boolean = false;

    get alertsList(): INotificationData[] {
        return this._alertsList.getValue();
      }    
    set alertsList(newValue: INotificationData[]) {
        const byId = new Map<string, INotificationData>();
        newValue.filter((alert) => this.canReceiveAlert(alert)).forEach((alert) => {
            byId.set(String(alert.id), normalizeAlert(alert));
        });
        this._alertsList.next([...byId.values()]);
    }
    get unitsTree(): (IUnit & {isOpen: boolean})[] {
        return this._unitsTree.getValue();
      }    
    set unitsTree(newValue: (IUnit & {isOpen: boolean})[]) {
        this._unitsTree.next(newValue);
    }
    get newAlert(): INotificationData | undefined {
        return this._newAlert.getValue();
      }    
    set newAlert(newValue: INotificationData) {
        this._newAlert.next(newValue);
    }
    get managedAlert(): INotificationData | undefined {
        return this._managedAlert.getValue();
      }    
    set managedAlert(newValue: INotificationData) {
        this._managedAlert.next(newValue);
    }
    private webSocketInstance?: WebSocketSubject<unknown>;
    private reconnectTimeoutId?: ReturnType<typeof setTimeout>;
    private connectedBuildingId?: string;
    private readonly managedAlertIds = new Set<string>();

    constructor(
        private loggerService: LoggerService,
        private requestsService: RequestsService,
        @Optional() private monitoringPreferences: MonitoringPreferencesService | null = null
    ) {
    }

    public canReceiveAlert(alert: INotificationData): boolean {
        const user = this.requestsService.getCurrentUser();
        const buildingId = alert.buildingId || alert.facilities?.building?.id;
        return !!alert.id && canDisplayAlertEventType(alert.eventType, user?.role)
            && !!user?.building?.id && buildingId === user.building.id && !alert.managedAt
            && !this.managedAlertIds.has(String(alert.id))
            && (!this.monitoringPreferences || this.monitoringPreferences.includes(buildingId, alert.facilities?.unit?.id));
    }

    addAlert(alert: INotificationData): boolean {
        if (!this.canReceiveAlert(alert) || this.alertsList.some((current) => current.id === String(alert.id))) {
            return false;
        }
        alert = normalizeAlert(alert);

        this.unitsTree.forEach((unit) => {
            (alert.facilities?.unit?.id === unit.id) && unit.rooms?.forEach((room) => {
                if ((alert.facilities?.room?.id === room.id) && room.devices?.some((device) => device.label === alert?.deviceId)) {
                    if (room.alert) {
                        room.alert = this.sortByTime([room.alert, alert])[0];
                    } else {
                        room.alert = alert;
                    }
                }
            })
        });
        this.alertsList = this.sortBySeverity([...this.alertsList, alert]);
        this.newAlert = alert;
        return true;
    }

    /** A successful management request on this device, including an already-managed response. */
    removeLocallyManagedAlert(alert: INotificationData): void {
        this.removeAlert(alert);
        // Emit even if WebSocket has already removed the alert before the PUT returns.
        this.locallyManagedAlert.next(alert);
    }

    removeAlert(alert: INotificationData) {
        const id = String(alert.id);
        const alreadyManaged = this.managedAlertIds.has(id);
        this.managedAlertIds.add(id);
        const existing = this.alertsList.find((current) => current.id === id);
        // Local management must update the badge before publishing the new list.
        // The same ID can be confirmed by both the PUT response and WebSocket.
        if (existing && !alreadyManaged && this.monitoringPreferences) {
            this.monitoringPreferences.setActiveCount(Math.max(0, this.activeAlertCount - 1),
                existing.buildingId || existing.facilities?.building?.id);
        }
        this.alertsList = this.alertsList.filter((currAlert) => currAlert.id !== id);
        this.unitsTree.forEach((unit) => {
            unit.rooms?.forEach((room) => {
                const currRoomAlerts = this.alertsList.filter((alertEl) => alertEl.id !== alert.id && room.devices?.some((device) => device.label === alertEl.deviceId))
                room.alert = currRoomAlerts.length ? this.sortByTime(currRoomAlerts)[0] : undefined;
            })
        });
        // A late local PUT response must not overwrite authoritative WebSocket management data.
        if (!alreadyManaged || alert.managedAt) {
            this.managedAlert = { ...existing, ...alert, id };
        }
    }

    public sortBySeverity(alertsList: INotificationData[]) {
      return alertsList.sort((a, b) => {
        const severityOrder = ['high', 'medium', 'low', 'offline', 'online'];
        const severityA = severityOrder.indexOf(a.severity);
        const severityB = severityOrder.indexOf(b.severity);
        if (severityA !== severityB) {
          return severityA - severityB; // Ordina per gravità
        } else {
          return b.timestamp.getTime() - a.timestamp.getTime(); // Ordina per timestamp (più recente prima)
        }
      });
    }

    public sortByTime(alertsList: INotificationData[]) {
        return alertsList.sort((a, b) => {
            return b.timestamp.getTime() - a.timestamp.getTime(); // Ordina per timestamp (più recente prima)
        });
      }

    clearAlerts() {
        this.managedAlertIds.clear();
        this._newAlert.next(undefined);
        this._managedAlert.next(undefined);
        this.alertsList = [];
        this.unitsTree = [];
    }

    startWss(toastService: ToastService) {
        const buildingId = this.requestsService.getCurrentUser()?.building?.id;
        if (buildingId) {
            if (this.webSocketInstance && !this.webSocketInstance.closed && this.connectedBuildingId === buildingId) {
                return;
            }
            this.stopWss();
            this.connectedBuildingId = buildingId;
            this.clearReconnectTimeout();
            this.loggerService.info("AVVIO CONNESSIONE - WSS NOTIFICHE")
            this.webSocketInstance = webSocket({
                url: environment.wsUrl+'notifications/ws'+'?buildingId='+encodeURIComponent(buildingId)+'&token='+encodeURIComponent(localStorage.getItem("ACCESS_TOKEN") || ''),
                openObserver: { next: () => this.connected$.next() },
                closeObserver: { next: (event: CloseEvent) => {
                    if (event.code === 4003 && ['MONITORING_SELECTION_CHANGED', 'MONITORING_UNIT_EXCLUDED'].includes(event.reason)) {
                        this.monitoringPreferences?.changed({ buildingId });
                    }
                } }
            });
            this.webSocketInstance?.subscribe({
                next: (msg: any) => {
                    switch (msg.type) {
                        case 'bed_state_updated':
                            if (this.canViewBedStates && msg.buildingId === this.requestsService.getCurrentUser()?.building?.id
                                && isBedState(msg.bed) && msg.bed.buildingId === msg.buildingId
                                && (!this.monitoringPreferences || this.monitoringPreferences.includes(msg.buildingId, msg.bed.unitId))) {
                                this.bedStateUpdates.next(msg as IBedStateUpdate);
                            }
                            // Posture updates must never touch alert counts, management or sounds.
                            return;
                        case 'monitoring_preferences_changed':
                            this.monitoringPreferences?.changed(msg);
                            break;
                        case 'notification':
                            this.addAlert(msg as INotificationData);
                            break;
                        case 'notification_managed':
                            if (msg.eventType && !canDisplayAlertEventType(msg.eventType, this.requestsService.getCurrentUser()?.role)) {
                                break;
                            }
                            this.removeAlert(msg as INotificationData);
                            msg.managedBy !== 'system' && toastService.showToast("Allerta Gestita", IonicColors.GREEN, 5000)
                        break;
                        case 'ping':
                            // Respond to server pings with a pong
                            this.webSocketInstance?.next({
                                type: 'pong',
                                timestamp: new Date().toISOString()
                            });
                            break;
                        case 'error':
                            this.loggerService.error(`ERRORE - WSS NOTIFICHE`);
                            this.webSocketInstance?.unsubscribe();
                            this.webSocketInstance = undefined;
                            this.scheduleReconnect(toastService, 5000);
                            break;
                        case 'device_online':
                            this.loggerService.info(`SENSORE ONLINE: ${msg.deviceId}`);
                            this.unitsTree = this.unitsTree.map((unit) => {
                                unit.rooms?.forEach((room) => {
                                    room.devices?.forEach((device) => {
                                        if (device.label === msg.deviceId) {
                                            device.status = 'online';
                                        }
                                    });
                                    room.offlineMode = room.devices?.some((device) => device.status === 'offline') ? (room.devices?.every((device) => device.status === 'offline') ? 'full' : 'partial') : undefined;
                                })
                                return unit;
                            })
                            break;
                        case 'device_offline':
                            this.loggerService.info(`SENSORE OFFLINE: ${msg.deviceId}`);
                            this.unitsTree = this.unitsTree.map((unit) => {
                                unit.rooms?.forEach((room) => {
                                    room.devices?.forEach((device) => {
                                        if (device.label === msg.deviceId) {
                                            device.status = 'offline';
                                        }
                                    });
                                    room.offlineMode = room.devices?.some((device) => device.status === 'offline') ? (room.devices?.every((device) => device.status === 'offline') ? 'full' : 'partial') : undefined;
                                })
                                return unit;
                            })
                            break;
                        default:
                        break;
                    }
                    // Server counts already include the managed event. Apply them
                    // after the local removal to avoid decrementing them twice.
                    this.monitoringPreferences?.setActiveCount(msg.activeAlertCount, msg.buildingId || buildingId);
                },
                error: (err: any) => {
                    this.loggerService.error("ERRORE - WSS NOTIFICHE");
                    this.webSocketInstance?.unsubscribe();
                    this.webSocketInstance = undefined;
                    this.scheduleReconnect(toastService, 3000);
                },
                complete: () => {
                    this.loggerService.info("CONNESSIONE CHIUSA - WSS NOTIFICHE");
                    this.webSocketInstance?.unsubscribe();
                    this.webSocketInstance = undefined;
                    document.visibilityState === 'visible' && this.scheduleReconnect(toastService, 3000);
                },
            });
        } else if (this.requestsService.getCurrentUser()?.role !== 'superuser') {
            this.loggerService.error("ERRORE - WSS NOTIFICHE - BUILDING_ID NON TROVATO")
        }
    }

    stopWss() {
        this.clearReconnectTimeout();
        this.webSocketInstance?.unsubscribe();
        this.webSocketInstance = undefined;
        this.connectedBuildingId = undefined;
        if (!this.requestsService.getCurrentUser()?.building?.id) {
            return;
        }
        this.loggerService.info("STOP CONNESSIONE - WSS NOTIFICHE");
    }

    private scheduleReconnect(toastService: ToastService, delayMs: number) {
        this.clearReconnectTimeout();
        this.reconnectTimeoutId = setTimeout(() => {
            this.reconnectTimeoutId = undefined;
            document.visibilityState === 'visible' && this.startWss(toastService);
        }, delayMs);
    }

    private clearReconnectTimeout() {
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = undefined;
        }
    }
}
